import { Component, Input, Output, EventEmitter, computed, signal, OnInit, OnDestroy } from '@angular/core';

import { cva } from '../utils/class-variance.util';
import { ThemeSection } from '../services/theme.service';
import { IconGlassComponent } from '../icon/icon-glass.component';
import { ToastData, ToastAction, ToastVariant, ToastPosition } from '../models/toast.model';

export { ToastData, ToastAction } from '../models/toast.model';

const toastVariants = cva({
  base: [
    'relative pointer-events-auto rounded-2xl border shadow-2xl max-w-sm w-full',
    'backdrop-blur-2xl transform-gpu transition-all duration-500 ease-out',
    'hover:scale-102 active:scale-98',
    // Glass morphism base
    'before:content-[""] before:absolute before:inset-0',
    'before:bg-glass-gradient-light dark:before:bg-glass-gradient-dark',
    'before:opacity-70 before:rounded-xl before:pointer-events-none',
    'before:transition-opacity before:duration-300',
  ].join(' '),
  variants: {
    variant: {
      default: [
        'bg-[radial-gradient(circle_at_top_right,rgba(37,99,235,0.12),transparent_34%),linear-gradient(180deg,rgba(24,40,66,0.96)_0%,rgba(10,18,34,0.98)_100%)]',
        'border-blue-200/16',
        'shadow-slate-950/42',
      ].join(' '),
      success: [
        'bg-[radial-gradient(circle_at_top_right,rgba(16,185,129,0.12),transparent_30%),linear-gradient(180deg,rgba(24,40,66,0.96)_0%,rgba(10,18,34,0.98)_100%)]',
        'border-emerald-300/24',
        'shadow-slate-950/42',
      ].join(' '),
      error: [
        'bg-[radial-gradient(circle_at_top_right,rgba(248,113,113,0.12),transparent_30%),linear-gradient(180deg,rgba(24,40,66,0.96)_0%,rgba(10,18,34,0.98)_100%)]',
        'border-red-300/24',
        'shadow-slate-950/42',
      ].join(' '),
      warning: [
        'bg-[radial-gradient(circle_at_top_right,rgba(251,191,36,0.12),transparent_30%),linear-gradient(180deg,rgba(24,40,66,0.96)_0%,rgba(10,18,34,0.98)_100%)]',
        'border-amber-300/24',
        'shadow-slate-950/42',
      ].join(' '),
      info: [
        'bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.14),transparent_32%),linear-gradient(180deg,rgba(24,40,66,0.96)_0%,rgba(10,18,34,0.98)_100%)]',
        'border-blue-300/24',
        'shadow-slate-950/42',
      ].join(' '),
      premium: [
        'bg-[radial-gradient(circle_at_top_right,rgba(37,99,235,0.16),transparent_34%),radial-gradient(circle_at_bottom_left,rgba(59,130,246,0.08),transparent_42%),linear-gradient(180deg,rgba(24,40,66,0.96)_0%,rgba(10,18,34,0.98)_100%)]',
        'border-[rgba(146,176,218,0.2)]',
        'shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_24px_56px_rgba(2,8,23,0.42),0_0_48px_rgba(37,99,235,0.08)]',
      ].join(' '),
    },
    state: {
      entering: 'animate-toast-enter',
      visible: 'animate-none',
      exiting: 'animate-toast-exit',
    },
  },
  defaultVariants: {
    variant: 'default',
    state: 'visible',
  },
});

const containerVariants = cva({
  base: ['fixed z-50 flex flex-col space-y-4 pointer-events-none', 'transition-all duration-300 ease-out'].join(' '),
  variants: {
    position: {
      'top-right': 'top-5 right-5',
      'top-left': 'top-5 left-5',
      'bottom-right': 'bottom-5 right-5',
      'bottom-left': 'bottom-5 left-5',
      'top-center': 'top-5 left-1/2 transform -translate-x-1/2',
      'bottom-center': 'bottom-5 left-1/2 transform -translate-x-1/2',
    },
  },
  defaultVariants: {
    position: 'bottom-right',
  },
});

@Component({
  selector: 'ui-toast-glass',
  standalone: true,
  imports: [IconGlassComponent],
  templateUrl: './toast-glass.component.html',
})
export class ToastGlassComponent implements OnInit, OnDestroy {
  // Single toast mode inputs
  @Input() title: string = '';
  @Input() message: string = '';
  @Input() variant: ToastVariant = 'default';
  @Input() duration: number = 5000;
  @Input() dismissible: boolean = true;
  @Input() actions: ToastAction[] = [];
  @Input() showProgress: boolean = false;
  @Input() themeSection: ThemeSection = 'global';

  // Container mode inputs
  @Input() position: ToastPosition = 'bottom-right';
  @Input() mode: 'single' | 'container' = 'single';

  @Output() dismissed = new EventEmitter<void>();
  @Output() actionClicked = new EventEmitter<ToastAction>();

  // State
  public toasts = signal<ToastData[]>([]);
  protected showSingle = computed(() => this.mode === 'single');
  protected progressWidth = signal(100);
  private progressTimer: ReturnType<typeof setInterval> | null = null;
  private dismissTimer: ReturnType<typeof setTimeout> | null = null;

  protected icon = computed(() => {
    const iconMap = {
      default: '',
      success: 'check-circle',
      error: 'alert-circle',
      warning: 'alert-triangle',
      info: 'info',
      premium: 'star',
    };
    return iconMap[this.variant];
  });

  protected computedToastClasses = computed(() => {
    return toastVariants({
      variant: this.variant,
      state: 'visible',
    });
  });

  protected computedContainerClasses = computed(() => {
    return containerVariants({
      position: this.position,
    });
  });

  protected iconContainerClasses = computed(() => {
    const baseClasses = 'flex items-center justify-center w-9 h-9 rounded-xl backdrop-blur-sm shrink-0 border';

    const variantClasses = {
      default: 'bg-blue-500/14 border-blue-300/18',
      success: 'bg-emerald-500/14 border-emerald-300/18',
      error: 'bg-red-500/14 border-red-300/18',
      warning: 'bg-amber-500/14 border-amber-300/18',
      info: 'bg-blue-500/14 border-blue-300/18',
      premium: 'bg-linear-to-br from-primary-500/24 to-primary-600/16 border-blue-300/18',
    };

    return `${baseClasses} ${variantClasses[this.variant]}`;
  });

  protected iconClasses = computed(() => {
    const variantClasses = {
      default: 'text-primary-600 dark:text-primary-400',
      success: 'text-cyan-600 dark:text-cyan-400',
      error: 'text-red-600 dark:text-red-400',
      warning: 'text-yellow-600 dark:text-yellow-400',
      info: 'text-blue-600 dark:text-blue-400',
      premium: 'text-primary-700 dark:text-primary-300',
    };

    return variantClasses[this.variant];
  });

  protected get ariaRole(): string {
    return this.variant === 'error' ? 'alert' : 'status';
  }

  protected get ariaLive(): string {
    return this.variant === 'error' ? 'assertive' : 'polite';
  }

  protected glowClasses = computed(() => {
    const variantClasses = {
      default: '',
      success: 'bg-cyan-500/5 animate-glass-pulse',
      error: 'bg-red-500/5 animate-glass-pulse',
      warning: 'bg-yellow-500/5 animate-glass-pulse',
      info: 'bg-blue-500/5 animate-glass-pulse',
      premium: 'bg-primary-500/5 animate-glass-pulse',
    };

    return `absolute inset-0 rounded-xl ${variantClasses[this.variant]}`;
  });

  ngOnInit(): void {
    if (this.mode === 'single' && this.duration > 0) {
      this.startProgressTimer();
      this.startDismissTimer();
    }
  }

  ngOnDestroy(): void {
    this.clearTimers();
  }

  // Single toast methods
  dismiss(): void {
    this.clearTimers();
    this.dismissed.emit();
  }

  protected handleActionClick(action: ToastAction): void {
    action.action?.();
    this.actionClicked.emit(action);
  }

  protected getActionClasses(action: ToastAction): string {
    const baseClasses = 'text-xs font-medium px-3 py-1.5 rounded-xl transition-all duration-200 border';

    if (action.variant === 'primary') {
      return `${baseClasses} bg-primary-500/18 border-blue-300/20 text-blue-100 hover:bg-primary-500/28!`;
    }

    return `${baseClasses} bg-white/6 border-blue-200/12 text-slate-200 hover:bg-white/10!`;
  }

  // Container/service methods
  addToast(toastData: ToastData): string {
    const id = toastData.id || Math.random().toString(36).substring(2, 11);
    const toast: ToastData = {
      ...toastData,
      id,
      variant: toastData.variant || 'default',
      duration: toastData.duration || 5000,
      dismissible: toastData.dismissible !== false,
    };

    this.toasts.update((toasts) => [...toasts, toast]);

    // Auto-dismiss if duration is set
    if (toast.duration && toast.duration > 0) {
      setTimeout(() => this.dismissToast(id), toast.duration);
    }

    return id;
  }

  dismissToast(id: string): void {
    this.toasts.update((toasts) => toasts.filter((t) => t.id !== id));
  }

  clearAllToasts(): void {
    this.toasts.set([]);
  }

  // Toast container helpers
  protected getToastClasses(toast: ToastData): string {
    return toastVariants({
      variant: toast.variant || 'default',
      state: 'visible',
    });
  }

  protected getToastIcon(toast: ToastData): string {
    const iconMap = {
      default: '',
      success: 'check-circle',
      error: 'alert-circle',
      warning: 'alert-triangle',
      info: 'info',
      premium: 'star',
    };
    return iconMap[toast.variant || 'default'];
  }

  protected getToastIconContainerClasses(toast: ToastData): string {
    const baseClasses = 'flex items-center justify-center w-8 h-8 rounded-lg backdrop-blur-sm shrink-0';

    const variantClasses = {
      default: 'bg-primary-500/20',
      success: 'bg-cyan-500/20',
      error: 'bg-red-500/20',
      warning: 'bg-yellow-500/20',
      info: 'bg-blue-500/20',
      premium: 'bg-linear-to-br from-primary-500/30 to-primary-600/20',
    };

    return `${baseClasses} ${variantClasses[toast.variant || 'default']}`;
  }

  protected getToastIconClasses(toast: ToastData): string {
    const variantClasses = {
      default: 'text-primary-600 dark:text-primary-400',
      success: 'text-cyan-600 dark:text-cyan-400',
      error: 'text-red-600 dark:text-red-400',
      warning: 'text-yellow-600 dark:text-yellow-400',
      info: 'text-blue-600 dark:text-blue-400',
      premium: 'text-primary-700 dark:text-primary-300',
    };

    return variantClasses[toast.variant || 'default'];
  }

  protected getToastAriaRole(toast: ToastData): string {
    return toast.variant === 'error' ? 'alert' : 'status';
  }

  protected getToastAriaLive(toast: ToastData): string {
    return toast.variant === 'error' ? 'assertive' : 'polite';
  }

  protected getToastGlowClasses(toast: ToastData): string {
    const variantClasses = {
      default: '',
      success: 'bg-cyan-500/5 animate-glass-pulse',
      error: 'bg-red-500/5 animate-glass-pulse',
      warning: 'bg-yellow-500/5 animate-glass-pulse',
      info: 'bg-blue-500/5 animate-glass-pulse',
      premium: 'bg-primary-500/5 animate-glass-pulse',
    };

    return `absolute inset-0 rounded-xl ${variantClasses[toast.variant || 'default']}`;
  }

  protected handleToastActionClick(action: ToastAction, toast: ToastData): void {
    action.action?.();
    this.actionClicked.emit(action);
    // Optionally dismiss toast after action
    if (toast.id) {
      this.dismissToast(toast.id);
    }
  }

  protected getToastActionClasses(action: ToastAction, _toast: ToastData): string {
    const baseClasses = 'text-xs font-medium px-3 py-1 rounded-lg transition-all duration-200';

    if (action.variant === 'primary') {
      return `${baseClasses} bg-primary-500/20 text-primary-700 dark:text-primary-300 hover:bg-primary-500/30!`;
    }

    return `${baseClasses} bg-white/20 text-gray-700 dark:text-gray-300 hover:bg-white/30!`;
  }

  // Timer helpers
  private startProgressTimer(): void {
    if (!this.showProgress) return;

    const interval = 100; // Update every 100ms
    const steps = this.duration / interval;
    let currentStep = 0;

    this.progressTimer = setInterval(() => {
      currentStep++;
      const progress = ((steps - currentStep) / steps) * 100;
      this.progressWidth.set(Math.max(0, progress));

      if (currentStep >= steps) {
        this.clearTimers();
      }
    }, interval);
  }

  private startDismissTimer(): void {
    this.dismissTimer = setTimeout(() => {
      this.dismiss();
    }, this.duration);
  }

  private clearTimers(): void {
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
      this.progressTimer = null;
    }
    if (this.dismissTimer) {
      clearTimeout(this.dismissTimer);
      this.dismissTimer = null;
    }
  }
}

/**
 * Usage Examples:
 *
 * Single toast (template-based):
 * <ui-toast-glass
 *   title="Success!"
 *   message="Your order has been placed successfully."
 *   variant="success"
 *   [duration]="5000"
 *   [showProgress]="true"
 *   (dismissed)="onToastDismissed()"
 * ></ui-toast-glass>
 *
 * Toast with actions:
 * <ui-toast-glass
 *   title="New Message"
 *   message="You have a new message from John."
 *   variant="info"
 *   [actions]="toastActions"
 * ></ui-toast-glass>
 *
 * Component setup:
 * toastActions: ToastAction[] = [
 *   {
 *     label: 'View',
 *     action: () => this.viewMessage(),
 *     variant: 'primary'
 *   },
 *   {
 *     label: 'Dismiss',
 *     action: () => this.dismissMessage(),
 *     variant: 'secondary'
 *   }
 * ];
 *
 * Container mode (for service-managed toasts):
 * <ui-toast-glass
 *   mode="container"
 *   position="top-right"
 * ></ui-toast-glass>
 *
 * // In service or component:
 * @ViewChild(ToastGlassComponent) toastContainer!: ToastGlassComponent;
 *
 * showSuccessToast() {
 *   this.toastContainer.addToast({
 *     title: 'Success',
 *     message: 'Operation completed successfully',
 *     variant: 'success',
 *     duration: 3000
 *   });
 * }
 *
 * E-commerce examples:
 *
 * // Product added to cart
 * <ui-toast-glass
 *   title="Added to Cart"
 *   message="iPhone 15 Pro has been added to your cart."
 *   variant="success"
 *   [actions]="[
 *     { label: 'View Cart', action: () => this.openCart(), variant: 'primary' },
 *     { label: 'Continue Shopping', action: () => this.continueShopping() }
 *   ]"
 * ></ui-toast-glass>
 *
 * // Order error
 * <ui-toast-glass
 *   title="Payment Failed"
 *   message="There was an issue processing your payment. Please try again."
 *   variant="error"
 *   [duration]="0"
 *   [actions]="[
 *     { label: 'Try Again', action: () => this.retryPayment(), variant: 'primary' },
 *     { label: 'Change Payment', action: () => this.changePayment() }
 *   ]"
 * ></ui-toast-glass>
 *
 * // Premium notification
 * <ui-toast-glass
 *   title="Premium Feature"
 *   message="Unlock exclusive benefits with our premium membership!"
 *   variant="premium"
 *   [actions]="[
 *     { label: 'Upgrade Now', action: () => this.upgradeToPremium(), variant: 'primary' }
 *   ]"
 * ></ui-toast-glass>
 *
 * Different variants:
 * <ui-toast-glass variant="success" message="Success message"></ui-toast-glass>
 * <ui-toast-glass variant="error" message="Error message"></ui-toast-glass>
 * <ui-toast-glass variant="warning" message="Warning message"></ui-toast-glass>
 * <ui-toast-glass variant="info" message="Info message"></ui-toast-glass>
 * <ui-toast-glass variant="premium" message="Premium message"></ui-toast-glass>
 */
